import { useMutation, type FetchResult } from '@apollo/client';
import type { ContentType, ClassificationResult } from '@classification-testing/domain';
import { ClassificationType } from '@classification-testing/domain';
import { CLASSIFY_WITH_BLOB_URL } from '@classification-testing/graphql';
import { addClassificationResult, setClassificationError, setClassificationLoading } from '@classification-testing/state/actions';
import { selectClassificationError } from '@classification-testing/state/selectors';
import { transformErrors } from '@core/adapters/errors';
import { track } from '@core/logging';
import { useMediaService } from '@core/services/MediaService';
import { useAppDispatch, useAppSelector, type AppDispatch } from '@store';
import { useCallback } from 'react';

interface ClassifyWithBlobUrlResponse {
  classifyContent: {
    text: string;
    elapsed: number;
    contentType: string;
    classificationType: string;
    confidenceScore: number;
    explanation?: string;
    contentUrl?: string;
    labels: Array<{
      severity: number;
      label_num_map: number;
      label_name: string;
      label_short_name: string;
      score?: number;
    }>;
    language?: string;
    languageDetected?: string;
    timestamp: string;
    policies?: Array<{
      id: string;
      type: string;
      created_by: string;
      created_at: string;
      last_modified_by: string;
      last_modified_at: string;
      community_id: string;
      is_enabled: boolean;
      name: string;
      description: string;
      labels: Array<{
        label_id: string;
        severity: number;
      }>;
    }>;
    non_triggered_policies?: Array<{
      id: string;
      type: string;
      created_by: string;
      created_at: string;
      last_modified_by: string;
      last_modified_at: string;
      community_id: string;
      is_enabled: boolean;
      name: string;
      description: string;
      labels: Array<{
        label_id: string;
        severity: number;
      }>;
    }>;
  };
}

export interface ClassifyWithBlobUrlVariables {
  input: {
    communityId: string;
    text?: string;
    imageName?: string;
    audioName?: string;
    promptVersion?: string;
  };
}

/**
 * @deprecated Use ClassifyWithBlobUrlVariables instead
 */
export interface ClassifyWithUploadVariables extends ClassifyWithBlobUrlVariables {
  input: ClassifyWithBlobUrlVariables['input'] & {
    /** @deprecated File uploads are now handled by Media Service */
    file?: File;
  };
}

type _MutationResult = FetchResult<ClassifyWithBlobUrlResponse>;

/**
 * Map blob URL classification GraphQL response to domain model
 */
function mapBlobUrlResponseToDomain(data: ClassifyWithBlobUrlResponse['classifyContent']) {
  const classificationType = (data.classificationType as ClassificationType) || ClassificationType.SAFE_CONTENT;

  // Use content type from response
  const contentType = data.contentType as ContentType;

  // Use the labels from the BFF which includes all label properties
  const labels = data.labels || [];

  const result: ClassificationResult = {
    id: Date.now().toString(),
    contentType,
    classificationType,
    labels,
    timestamp: data.timestamp,
    confidenceScore: data.confidenceScore,
    language: data.language,
    languageDetected: data.languageDetected,
    languageClassified: data.language, // Language used for classification
    text: data.text,
    explanation: data.explanation,
    contentUrl: data.contentUrl,
    policies: data.policies,
    nonTriggeredPolicies: data.non_triggered_policies,
  };

  return result;
}

/**
 * Helper function to handle GraphQL errors
 */
function handleGraphQLErrors(result: _MutationResult, dispatch: AppDispatch) {
  if (result.errors && result.errors.length > 0) {
    const errors = transformErrors(result.errors[0]);

    dispatch(setClassificationError(errors));
    throw errors;
  }

  if (!result.data?.classifyContent) {
    const errors = transformErrors(new Error('No data returned from GraphQL mutation'));

    dispatch(setClassificationError(errors));
    throw errors;
  }

  // The simplified response structure doesn't have an error field
  // Errors will be handled by GraphQL errors above
}

export function useClassifyWithUpload(communityId?: string, promptVersion?: string) {
  const dispatch = useAppDispatch();
  const errors = useAppSelector(selectClassificationError);
  const { uploadFile } = useMediaService();

  // Blob URL classification GraphQL mutation
  const [classifyMutation, { loading: classifyLoading }] = useMutation<ClassifyWithBlobUrlResponse, ClassifyWithBlobUrlVariables>(CLASSIFY_WITH_BLOB_URL);

  // Two-step file upload and classification function
  const classifyWithUpload = useCallback(
    // eslint-disable-next-line max-statements, complexity
    async (file: File) => {
      track('ClassifyContentWithUploadInitiated', {
        communityId: communityId || 'unknown',
        fileType: file.type,
        fileSize: file.size.toString(),
        promptVersion: promptVersion || 'default',
      });

      if (!communityId) {
        const errors = transformErrors(new Error('Community ID is required for classification'));

        dispatch(setClassificationError(errors));
        throw errors;
      }

      // Clear previous errors and set loading state
      dispatch(setClassificationError(null));
      dispatch(setClassificationLoading(true));

      try {
        // Step 1: Upload file to Media Service
        // Note: Using session-based authentication - the MediaService will use cookies
        const uploadResult = await uploadFile({
          file,
          communityId,
          title: file.name,
          description: 'File for classification testing',
        }); // Using session-based auth, no access token needed

        // Step 2: Classify using blob URL
        const classifyInput: ClassifyWithBlobUrlVariables['input'] = {
          communityId,
          ...(promptVersion && { promptVersion }),
        };

        // Determine file type and set appropriate name field with blob URL
        if (file.type.startsWith('audio/')) {
          classifyInput.audioName = uploadResult.fileUrl;
        } else if (file.type.startsWith('image/')) {
          classifyInput.imageName = uploadResult.fileUrl;
        } else {
          throw new Error(`Unsupported file type: ${file.type}`);
        }

        const result = await classifyMutation({
          variables: {
            input: classifyInput,
          },
        });

        // Handle all GraphQL errors
        handleGraphQLErrors(result, dispatch);

        // Map GraphQL response to domain model (data is guaranteed to exist after handleGraphQLErrors)
        if (!result.data?.classifyContent) {
          const errors = transformErrors(new Error('Data validation failed after error handling'));

          dispatch(setClassificationError(errors));
          throw errors;
        }

        const mappedResult = mapBlobUrlResponseToDomain(result.data.classifyContent);

        // Add the result to Redux state for consistency
        // communityId is guaranteed to be non-null at this point due to the check at the beginning of the function
        dispatch(addClassificationResult({ communityId, result: mappedResult }));
        dispatch(setClassificationLoading(false));

        track('ClassifyContentWithUploadSucceeded', {
          communityId: communityId || 'unknown',
          fileType: file.type,
          fileSize: file.size.toString(),
          promptVersion: promptVersion || 'default',
        });

        return mappedResult;
      } catch (error) {
        const errors = transformErrors(error);

        track('ClassifyContentWithUploadFailed', {
          communityId: communityId || 'unknown',
          fileType: file.type,
          fileSize: file.size.toString(),
          promptVersion: promptVersion || 'default',
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        dispatch(setClassificationError(errors));
        dispatch(setClassificationLoading(false));
        throw errors;
      }
    },
    [classifyMutation, uploadFile, communityId, dispatch, promptVersion]
  );

  return {
    classifyWithUpload,
    loading: classifyLoading,
    errors,
  };
}
